'use client';

import React, { useState } from "react";
import { Flame } from 'lucide-react';
import { siteConfig } from '@/lib/config/site-config';
import { useLanguage } from '@/lib/config/useLanguage';
import { usePathname, useRouter } from 'next/navigation';
import { getBranchCodeFromPath, regionalizePath } from '@/lib/config/branches-config';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { buildLeadThankYouUrl, clearPendingLeadSubmissionId, extractOptionalLeadId, getOrCreatePendingLeadSubmissionId } from '@/lib/forms/lead-thank-you';
import { useInvisibleSmartCaptcha } from '@/components/SmartCaptcha';

const FORM_TYPE = 'project-calculation';

// Статический массив пунктов меню
const menuItems = [
    {
        id: 'promotions',
        name: 'Акции',
        url: '/sales',
        icon: Flame,
        highlighted: true,
        color: '#ef4444'
    },
    {
        id: 'portfolio',
        name: 'Примеры наших',
        url: '/portfolio',
        icon: null
    },
    {
        id: 'blog',
        name: 'Блог',
        url: '/blog',
        icon: null
    },
    {
        id: 'faq',
        name: 'Частые вопросы',
        url: '/company/faq',
        icon: null
    },
    {
        id: 'contacts',
        name: 'Контакты',
        url: '/company/contacts',
        icon: null
    }
];

export default function TopSubMenu() {
    const { localizePath } = useLanguage();
    const pathname = usePathname() || '/';
    const router = useRouter();
    const currentBranchCode = getBranchCodeFromPath(pathname);
    const [isCalculationModalOpen, setIsCalculationModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        message: '',
        privacy: false,
        marketing: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState('idle'); // 'idle' | 'success' | 'error'
    const [submitMessage, setSubmitMessage] = useState('');
    const { captchaElement, executeCaptcha, resetCaptcha } = useInvisibleSmartCaptcha();

    // Проверяем, включено ли меню в настройках
    if (!siteConfig.features.menu.enabled) {
        return null;
    }

    // Функция для комбинирования языковой и региональной локализации
    const localizeAndRegionalizePath = (path) => {
        const localizedPath = localizePath(path || '/');
        return regionalizePath(localizedPath, currentBranchCode);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = (name, checked) => {
        setFormData((prev) => ({ ...prev, [name]: checked }));
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        
        // Проверка обязательных полей
        if (!formData.privacy) {
            setSubmitStatus('error');
            setSubmitMessage('Необходимо согласие на обработку персональных данных');
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus('idle');
        setSubmitMessage('');

        try {
            const submissionId = getOrCreatePendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
            const smartToken = await executeCaptcha();
            const response = await fetch('/api/forms', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formData.name,
                    phone: formData.phone,
                    email: formData.email,
                    message: formData.message,
                    privacy: formData.privacy,
                    marketing: formData.marketing,
                    formType: FORM_TYPE,
                    pageUrl: typeof window !== 'undefined' ? window.location.href : '',
                    smartToken,
                    submissionId,
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                const thankYouUrl = buildLeadThankYouUrl({
                    pathname: window.location.pathname,
                    search: window.location.search,
                    formType: FORM_TYPE,
                    leadId: extractOptionalLeadId(data),
                    sid: submissionId,
                });

                clearPendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
                router.push(thankYouUrl);
                return;
            } else {
                setSubmitStatus('error');
                setSubmitMessage(data.message || 'Ошибка при отправке заявки');
            }
        } catch (error) {
            setSubmitStatus('error');
            setSubmitMessage('Произошла ошибка. Попробуйте позже.');
            console.error('Ошибка при отправке формы:', error);
        } finally {
            resetCaptcha();
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="md:flex hidden flex-col items-center justify-center bg-header-gradient">
                <div className="w-full max-w-[1536px] px-4">
                    <hr className="border-white/20"/>
                    <div className="hidden sm:flex gap-2 mt-1 pb-1 items-center justify-between">
                        <div className="flex gap-2">
                            {menuItems.map(item => {
                                const IconComponent = item.icon;
                                const isHighlighted = item.highlighted;
                                
                                return (
                                    <a
                                        key={item.id}
                                        className={`flex text-sm items-center gap-2 hover:bg-white/20 px-2 py-1 rounded ${
                                            isHighlighted 
                                                ? 'text-red-300 font-semibold hover:text-red-200' 
                                                : 'text-white hover:text-white/80'
                                        }`}
                                        href={localizeAndRegionalizePath(item.url)}>
                                        {IconComponent && (
                                            <IconComponent 
                                                size={16} 
                                                color={isHighlighted ? '#fca5a5' : '#ffffff'}
                                            />
                                        )}
                                        <span>{item.name}</span>
                                    </a>
                                );
                            })}
                        </div>
                        <div className="flex gap-2 ml-4">
                            <Button
                                asChild
                                variant="default"
                                className="text-white"
                                style={{ backgroundColor: '#10253d' }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0d1e2f'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10253d'}
                            >
                                <a href={localizeAndRegionalizePath('/services')}>
                                    Быстровозводимые здания
                                </a>
                            </Button>
                            <Button
                                variant="destructive"
                                className="bg-red-600 hover:bg-red-700 text-white"
                                onClick={() => setIsCalculationModalOpen(true)}
                            >
                                Расчёт по проекту
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Модальное окно расчёта по проекту */}
            <Dialog open={isCalculationModalOpen} onOpenChange={setIsCalculationModalOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Расчёт по проекту</DialogTitle>
                        <DialogDescription>
                            Заполните форму, и мы свяжемся с вами в ближайшее время для расчёта стоимости проекта.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleFormSubmit} className="mt-4 space-y-4">
                        <div>
                            <Label htmlFor="calc-name" className="text-sm font-medium text-gray-700">
                                ФИО <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="calc-name"
                                name="name"
                                type="text"
                                required
                                value={formData.name}
                                onChange={handleFormChange}
                                className="mt-2"
                                placeholder="Иванов Иван Иванович"
                            />
                        </div>
                        <div>
                            <Label htmlFor="calc-phone" className="text-sm font-medium text-gray-700">
                                Телефон <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="calc-phone"
                                name="phone"
                                type="tel"
                                required
                                value={formData.phone}
                                onChange={handleFormChange}
                                className="mt-2"
                                placeholder="+7 (999) 123-45-67"
                            />
                        </div>
                        <div>
                            <Label htmlFor="calc-email" className="text-sm font-medium text-gray-700">
                                Email <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="calc-email"
                                name="email"
                                type="email"
                                required
                                value={formData.email}
                                onChange={handleFormChange}
                                className="mt-2"
                                placeholder="example@mail.com"
                            />
                        </div>
                        <div>
                            <Label htmlFor="calc-message" className="text-sm font-medium text-gray-700">
                                Описание проекта
                            </Label>
                            <textarea
                                id="calc-message"
                                name="message"
                                value={formData.message}
                                onChange={handleFormChange}
                                className="mt-2 flex min-h-[100px] w-full rounded-md border border-slate-200 bg-transparent px-3 py-2 text-base shadow-sm transition-colors placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:border-slate-800 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300"
                                placeholder="Опишите ваш проект, укажите размеры, назначение здания и другие важные детали"
                                rows={4}
                            />
                        </div>

                        {/* Чекбоксы согласия */}
                        <div className="space-y-3">
                            <div className="flex items-start">
                                <Checkbox
                                    id="calc-privacy"
                                    checked={formData.privacy}
                                    onCheckedChange={(checked) => handleCheckboxChange('privacy', checked)}
                                    required
                                    className="mt-1"
                                />
                                <Label htmlFor="calc-privacy" className="ml-2 text-sm text-gray-600">
                                    Я даю согласие на обработку своих персональных данных и соглашаюсь с{' '}
                                    <a href={localizePath('/pages/politika-konfidentsialnosti-personalnykh-dannykh')} className="text-blue-600 hover:underline">
                                        политикой конфиденциальности
                                    </a>
                                    . <span className="text-red-500">*</span>
                                </Label>
                            </div>

                            <div className="flex items-start">
                                <Checkbox
                                    id="calc-marketing"
                                    checked={formData.marketing}
                                    onCheckedChange={(checked) => handleCheckboxChange('marketing', checked)}
                                    className="mt-1"
                                />
                                <Label htmlFor="calc-marketing" className="ml-2 text-sm text-gray-600">
                                    Я даю согласие на направление рекламных рассылок.
                                </Label>
                            </div>
                        </div>

                        {/* Сообщения о результате */}
                        {submitStatus === 'success' && (
                            <div className="flex items-center gap-2 rounded-lg bg-green-50 p-4 text-green-800">
                                <CheckCircle2 className="h-5 w-5 shrink-0" />
                                <p className="text-sm font-medium">{submitMessage}</p>
                            </div>
                        )}

                        {submitStatus === 'error' && (
                            <div className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-red-800">
                                <AlertCircle className="h-5 w-5 shrink-0" />
                                <p className="text-sm font-medium">{submitMessage}</p>
                            </div>
                        )}

                        {captchaElement}

                        <div className="flex justify-end gap-2 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setIsCalculationModalOpen(false);
                                    setSubmitStatus('idle');
                                    setSubmitMessage('');
                                }}
                                disabled={isSubmitting}
                            >
                                Отмена
                            </Button>
                            <Button
                                type="submit"
                                variant="default"
                                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                disabled={isSubmitting || !formData.privacy}
                            >
                                {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
